import React from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import './ArrRadio.scss';
import ArrTooltip from '../arr-tooltip/ArrTooltip';

interface RadioData {
  label: string;
  value: string; // keeping value as string for consistency
  tooltip?: string;
}

export interface RadioButtonProps {
  variant?: 'primary' | 'secondary';
  size?: 'large' | 'medium' | 'small' | 'xs';
  disabled?: boolean;
  onChange: (value: string) => void;
  label?: string;
  value: string | null;
  name: string;
  ariaLabel?: string;
  id?: string;
  data: RadioData[];
  dataTestId: string;
  className?: string;
  tabIndex?: number;
  isAriaDisabled?: boolean;
  uncheckedTabIndex?: number;
  labelTabIndex?: number;
}

const CustomCheckedIcon = ({dataTestId}: {dataTestId: string}) => {
  return (
    <span
      className="radio-icon radio-icon-checked"
      data-testid={`arr-radio-checked-${dataTestId}`}
    />
  );
};

const CustomUncheckedIcon = ({
  dataTestId,
  onKeyDown,
  uncheckedTabIndex,
}: {
  dataTestId: string;
  onKeyDown: (e: React.KeyboardEvent) => void;
  uncheckedTabIndex?: number;
}) => {
  return (
    <span
      className="radio-icon radio-icon-unchecked"
      tabIndex={uncheckedTabIndex}
      onKeyDown={onKeyDown}
      data-testid={`arr-radio-unchecked-${dataTestId}`}
    />
  );
};

interface ArrRadioProps extends RadioButtonProps {
  classNameFormControlLabel?: string;
  row?: boolean;
}

const ArrRadio: React.FC<ArrRadioProps> = ({
  size = 'large',
  value,
  onChange,
  label,
  name = 'controlled-radio-buttons-group',
  data,
  className = '',
  ariaLabel,
  disabled = false,
  classNameFormControlLabel = '',
  row = true,
  tabIndex,
  dataTestId,
  isAriaDisabled = false,
  uncheckedTabIndex,
  labelTabIndex,
}) => {
  const labelId = `${name}-label`;
  return (
    <FormControl
      component="fieldset"
      className={`arr-radio arr-radio--${size} ${className}`}
    >
      {label ? <FormLabel id={labelId}>{label}</FormLabel> : null}
      <RadioGroup
        row={row}
        aria-disabled={isAriaDisabled}
        aria-labelledby={label ? labelId : undefined}
        aria-label={!label ? ariaLabel : undefined}
        name={name}
        value={value ?? undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        data-testid={dataTestId}
      >
        {data?.map((dataOption, index) => (
          <FormControlLabel
            //  data-testid={${index}{String(dataOption.value)}}
            key={String(dataOption.label)}
            value={String(dataOption.value)}
            // slotProps={{typography: {'data-testid': 'testing'}}}
            control={
              <Radio
                disableRipple
                className={`${isAriaDisabled ? 'custom-radio Mui-disabled' : 'custom-radio'}`}
                icon={
                  <CustomUncheckedIcon
                    dataTestId={`${dataTestId}-${index}`}
                    uncheckedTabIndex={uncheckedTabIndex}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onChange(dataOption.value);
                        // after changing value, focus the native input so focus styling is correct
                        const testId = `${dataTestId}-option-${index + 1}`;
                        requestAnimationFrame(() => {
                          const el = document.querySelector(
                            `[data-testid="${testId}"]`
                          ) as HTMLElement | null;
                          el?.focus();
                        });
                      }
                    }}
                  />
                }
                checkedIcon={
                  <CustomCheckedIcon dataTestId={`${dataTestId}-${index}`} />
                }
                disabled={disabled}
                classes={{root: 'custom-radio'}}
                slotProps={{
                  input: {
                    'data-testid': `${dataTestId}-option-${index + 1}`,
                  },
                }}
                // slotProps={{
                //   input: {
                //     'data-testid': `radio-${value}-option-${index + 1}`,
                //   } as React.InputHTMLAttributes<HTMLInputElement>,
                // }}
                {...(tabIndex ? {tabIndex: tabIndex} : {})}
              />
            }
            label={
              dataOption.label ? (
                <ArrTooltip
                  content={dataOption.tooltip}
                  showOnTruncate={dataOption.tooltip === ''}
                  isTruncated={false}
                  labelId={`label-${dataOption.label?.replaceAll(' ', '-')}`}
                  focusableWrapper={tabIndex === -1 ? true : false}
                  tabIndex={labelTabIndex}
                >
                  {dataOption.label}
                </ArrTooltip>
              ) : null
            }
            className={classNameFormControlLabel}
            data-testid={dataOption.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

export default ArrRadio;
